import { rawClient, toApiError } from "./client";
import { ApiError } from "./errors";
import type {
  Attachment,
  BatchGetInstanceSettingsResponse,
  BatchGetLinkMetadataResponse,
  CreatePersonalAccessTokenResponse,
  GetCurrentUserResponse,
  IdentityProvider,
  InstanceProfile,
  InstanceSetting,
  InstanceStats,
  LinkedIdentity,
  LinkMetadata,
  ListAllUserStatsResponse,
  ListAttachmentsResponse,
  ListIdentityProvidersResponse,
  ListLinkedIdentitiesResponse,
  ListMemoAttachmentsResponse,
  ListMemoCommentsResponse,
  ListMemoReactionsResponse,
  ListMemoRelationsResponse,
  ListMemosResponse,
  ListMemoSharesResponse,
  ListPersonalAccessTokensResponse,
  ListShortcutsResponse,
  ListUserNotificationsResponse,
  ListUserSettingsResponse,
  ListUsersResponse,
  ListUserWebhooksResponse,
  Memo,
  MemoShare,
  Reaction,
  RefreshTokenResponse,
  Shortcut,
  SignInRequest,
  SignInResponse,
  User,
  UserNotification,
  UserSetting,
  UserStats,
  UserWebhook,
} from "./types";
import type { operations } from "./schema";

/** Unwrap an openapi-fetch result: return data or throw a normalized ApiError. */
function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error !== undefined && result.error !== null) {
    throw toApiError(result.error, result.response);
  }
  if (result.data === undefined) {
    // Successful responses with empty bodies (e.g. deletes) deserialize as undefined.
    return undefined as T;
  }
  return result.data;
}

type Query = Record<string, string | number | boolean | undefined>;

function instanceSettingId(setting: string): string {
  const resourceId = setting.split("/").filter(Boolean).at(-1) ?? setting;
  return resourceId.replaceAll("-", "_").toUpperCase();
}

function instanceSettingName(setting: string): string {
  return `instance/settings/${instanceSettingId(setting)}`;
}

// ===========================================================================
// Instance
// ===========================================================================

export const instanceService = {
  async getProfile(): Promise<InstanceProfile> {
    return unwrap(await rawClient.GET("/api/v1/instance/profile", {}));
  },
  async getSetting(setting: string): Promise<InstanceSetting> {
    return unwrap(
      await rawClient.GET("/api/v1/instance/settings/{setting}", {
        params: { path: { setting: instanceSettingId(setting) } },
      }),
    );
  },
  async batchGetSettings(
    names: string[],
  ): Promise<BatchGetInstanceSettingsResponse> {
    return unwrap(
      await rawClient.POST("/api/v1/instance/settings:batchGet", {
        body: { names: names.map(instanceSettingName) },
      }),
    );
  },
  async updateSetting(
    setting: string,
    body: InstanceSetting,
    updateMask: string,
  ): Promise<InstanceSetting> {
    return unwrap(
      await rawClient.PATCH("/api/v1/instance/settings/{setting}", {
        params: {
          path: { setting: instanceSettingId(setting) },
          query: { updateMask },
        },
        body,
      }),
    );
  },
  async getStats(): Promise<InstanceStats> {
    return unwrap(await rawClient.GET("/api/v1/instance/stats", {}));
  },
  async testEmailSetting(body: Record<string, unknown>): Promise<void> {
    return unwrap(
      await rawClient.POST("/api/v1/instance/settings/notification:testEmail", {
        body: body as never,
      }),
    );
  },
};

// ===========================================================================
// Auth
// ===========================================================================

export const authService = {
  async signIn(body: SignInRequest): Promise<SignInResponse> {
    return unwrap(
      await rawClient.POST("/api/v1/auth/signin", { body: body as never }),
    );
  },
  async refresh(): Promise<RefreshTokenResponse> {
    return unwrap(await rawClient.POST("/api/v1/auth/refresh", { body: {} }));
  },
  async getCurrentUser(): Promise<GetCurrentUserResponse> {
    return unwrap(await rawClient.GET("/api/v1/auth/me", {}));
  },
  async signOut(): Promise<void> {
    return unwrap(await rawClient.POST("/api/v1/auth/signout", {}));
  },
};

// ===========================================================================
// Memos
// ===========================================================================

type ListMemosParams =
  operations["MemoService_ListMemos"]["parameters"]["query"];

export const memoService = {
  async list(params: ListMemosParams): Promise<ListMemosResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos", {
        params: { query: params as Query },
      }),
    );
  },
  async get(memo: string): Promise<Memo> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}", {
        params: { path: { memo } },
      }),
    );
  },
  async create(body: Partial<Memo>, memoId?: string): Promise<Memo> {
    return unwrap(
      await rawClient.POST("/api/v1/memos", {
        params: { query: memoId ? { memoId } : undefined },
        body: body as Memo,
      }),
    );
  },
  async update(
    memo: string,
    body: Partial<Memo>,
    updateMask: string,
  ): Promise<Memo> {
    return unwrap(
      await rawClient.PATCH("/api/v1/memos/{memo}", {
        params: { path: { memo }, query: { updateMask } },
        body: body as Memo,
      }),
    );
  },
  async remove(memo: string, force?: boolean): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/memos/{memo}", {
        params: { path: { memo }, query: force ? { force } : undefined },
      }),
    );
  },

  // Comments
  async listComments(
    memo: string,
    pageToken?: string,
    pageSize?: number,
  ): Promise<ListMemoCommentsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}/comments", {
        params: { path: { memo }, query: { pageToken, pageSize } as Query },
      }),
    );
  },
  async createComment(
    memo: string,
    body: Partial<Memo>,
    commentId?: string,
  ): Promise<Memo> {
    return unwrap(
      await rawClient.POST("/api/v1/memos/{memo}/comments", {
        params: {
          path: { memo },
          query: commentId ? { commentId } : undefined,
        },
        body: body as Memo,
      }),
    );
  },

  // Reactions
  async listReactions(memo: string): Promise<ListMemoReactionsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}/reactions", {
        params: { path: { memo } },
      }),
    );
  },
  async upsertReaction(memo: string, reactionType: string): Promise<Reaction> {
    return unwrap(
      await rawClient.POST("/api/v1/memos/{memo}/reactions", {
        params: { path: { memo } },
        body: {
          name: `memos/${memo}`,
          reaction: { contentId: `memos/${memo}`, reactionType },
        },
      }),
    );
  },
  async deleteReaction(memo: string, reaction: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/memos/{memo}/reactions/{reaction}", {
        params: { path: { memo, reaction } },
      }),
    );
  },

  // Relations (full-set replace)
  async listRelations(memo: string): Promise<ListMemoRelationsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}/relations", {
        params: { path: { memo } },
      }),
    );
  },
  async setRelations(
    memo: string,
    relations: Memo["relations"],
  ): Promise<void> {
    return unwrap(
      await rawClient.PATCH("/api/v1/memos/{memo}/relations", {
        params: { path: { memo } },
        body: { relations: relations ?? [] } as never,
      }),
    );
  },

  // Attachments binding (full-set replace)
  async listAttachments(memo: string): Promise<ListMemoAttachmentsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}/attachments", {
        params: { path: { memo } },
      }),
    );
  },
  async setAttachments(memo: string, attachments: string[]): Promise<void> {
    return unwrap(
      await rawClient.PATCH("/api/v1/memos/{memo}/attachments", {
        params: { path: { memo } },
        body: { attachments: attachments.map((name) => ({ name })) } as never,
      }),
    );
  },

  // Shares
  async listShares(memo: string): Promise<ListMemoSharesResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/{memo}/shares", {
        params: { path: { memo } },
      }),
    );
  },
  async createShare(memo: string, expireTime?: string): Promise<MemoShare> {
    return unwrap(
      await rawClient.POST("/api/v1/memos/{memo}/shares", {
        params: { path: { memo } },
        body: (expireTime ? { expireTime } : {}) as MemoShare,
      }),
    );
  },
  async deleteShare(memo: string, share: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/memos/{memo}/shares/{share}", {
        params: { path: { memo, share } },
      }),
    );
  },
  async getByShare(shareId: string): Promise<Memo> {
    return unwrap(
      await rawClient.GET("/api/v1/shares/{shareId}", {
        params: { path: { shareId } },
      }),
    );
  },

  // Link metadata
  async getLinkMetadata(url: string): Promise<LinkMetadata> {
    return unwrap(
      await rawClient.GET("/api/v1/memos/-/linkMetadata", {
        params: { query: { url } },
      }),
    );
  },
  async batchGetLinkMetadata(
    urls: string[],
  ): Promise<BatchGetLinkMetadataResponse> {
    return unwrap(
      await rawClient.POST("/api/v1/memos/-/linkMetadata:batchGet", {
        body: { urls } as never,
      }),
    );
  },
};

// ===========================================================================
// Attachments
// ===========================================================================

type ListAttachmentsParams =
  operations["AttachmentService_ListAttachments"]["parameters"]["query"];

export const attachmentService = {
  async list(params: ListAttachmentsParams): Promise<ListAttachmentsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/attachments", {
        params: { query: params as Query },
      }),
    );
  },
  async create(
    body: Partial<Attachment>,
    attachmentId?: string,
  ): Promise<Attachment> {
    return unwrap(
      await rawClient.POST("/api/v1/attachments", {
        params: { query: attachmentId ? { attachmentId } : undefined },
        body: body as Attachment,
      }),
    );
  },
  async get(attachment: string): Promise<Attachment> {
    return unwrap(
      await rawClient.GET("/api/v1/attachments/{attachment}", {
        params: { path: { attachment } },
      }),
    );
  },
  async download(attachment: string, filename: string): Promise<Blob> {
    return unwrap(
      await rawClient.GET("/file/attachments/{uid}/{filename}", {
        params: { path: { uid: attachment, filename } },
        parseAs: "blob",
      }),
    );
  },
  async update(
    attachment: string,
    body: Partial<Attachment>,
    updateMask: string,
  ): Promise<Attachment> {
    return unwrap(
      await rawClient.PATCH("/api/v1/attachments/{attachment}", {
        params: { path: { attachment }, query: { updateMask } },
        body: body as Attachment,
      }),
    );
  },
  async remove(attachment: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/attachments/{attachment}", {
        params: { path: { attachment } },
      }),
    );
  },
  async batchDelete(names: string[]): Promise<void> {
    return unwrap(
      await rawClient.POST("/api/v1/attachments:batchDelete", {
        body: { names } as never,
      }),
    );
  },
};

// ===========================================================================
// Users / settings / notifications / PAT / webhooks / linked identities
// ===========================================================================

export const userService = {
  async list(params?: {
    pageSize?: number;
    pageToken?: string;
    filter?: string;
  }): Promise<ListUsersResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users", {
        params: { query: params as Query },
      }),
    );
  },
  async create(
    body: Partial<User>,
    opts?: { userId?: string; validateOnly?: boolean },
  ): Promise<User> {
    return unwrap(
      await rawClient.POST("/api/v1/users", {
        params: { query: opts as Query },
        body: body as User,
      }),
    );
  },
  async get(user: string): Promise<User> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}", {
        params: { path: { user } },
      }),
    );
  },
  async update(
    user: string,
    body: Partial<User>,
    updateMask: string,
    allowMissing?: boolean,
  ): Promise<User> {
    return unwrap(
      await rawClient.PATCH("/api/v1/users/{user}", {
        params: {
          path: { user },
          query: { updateMask, allowMissing } as Query,
        },
        body: body as User,
      }),
    );
  },
  async remove(user: string, force?: boolean): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/users/{user}", {
        params: { path: { user }, query: force ? { force } : undefined },
      }),
    );
  },
  async getStats(user: string): Promise<UserStats> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}:getStats", {
        params: { path: { user } },
      }),
    );
  },
  async listAllStats(): Promise<ListAllUserStatsResponse> {
    return unwrap(await rawClient.GET("/api/v1/users:stats", {}));
  },

  // Settings
  async listSettings(user: string): Promise<ListUserSettingsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/settings", {
        params: { path: { user } },
      }),
    );
  },
  async getSetting(user: string, setting: string): Promise<UserSetting> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/settings/{setting}", {
        params: { path: { user, setting } },
      }),
    );
  },
  async updateSetting(
    user: string,
    setting: string,
    body: UserSetting,
    updateMask: string,
  ): Promise<UserSetting> {
    return unwrap(
      await rawClient.PATCH("/api/v1/users/{user}/settings/{setting}", {
        params: { path: { user, setting }, query: { updateMask } },
        body,
      }),
    );
  },

  // Notifications
  async listNotifications(
    user: string,
    params?: { pageSize?: number; pageToken?: string; filter?: string },
  ): Promise<ListUserNotificationsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/notifications", {
        params: { path: { user }, query: params as Query },
      }),
    );
  },
  async updateNotification(
    user: string,
    notification: string,
    body: Partial<UserNotification>,
    updateMask: string,
  ): Promise<UserNotification> {
    return unwrap(
      await rawClient.PATCH(
        "/api/v1/users/{user}/notifications/{notification}",
        {
          params: { path: { user, notification }, query: { updateMask } },
          body: body as UserNotification,
        },
      ),
    );
  },
  async deleteNotification(user: string, notification: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE(
        "/api/v1/users/{user}/notifications/{notification}",
        {
          params: { path: { user, notification } },
        },
      ),
    );
  },

  // Personal access tokens
  async listPersonalAccessTokens(
    user: string,
  ): Promise<ListPersonalAccessTokensResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/personalAccessTokens", {
        params: { path: { user } },
      }),
    );
  },
  async createPersonalAccessToken(
    user: string,
    body: { description?: string; expiresInDays?: number },
  ): Promise<CreatePersonalAccessTokenResponse> {
    return unwrap(
      await rawClient.POST("/api/v1/users/{user}/personalAccessTokens", {
        params: { path: { user } },
        body: { parent: `users/${user}`, ...body } as never,
      }),
    );
  },
  async deletePersonalAccessToken(user: string, token: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE(
        "/api/v1/users/{user}/personalAccessTokens/{personalAccessToken}",
        {
          params: { path: { user, personalAccessToken: token } },
        },
      ),
    );
  },

  // Webhooks
  async listWebhooks(user: string): Promise<ListUserWebhooksResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/webhooks", {
        params: { path: { user } },
      }),
    );
  },
  async createWebhook(
    user: string,
    body: Partial<UserWebhook>,
  ): Promise<UserWebhook> {
    return unwrap(
      await rawClient.POST("/api/v1/users/{user}/webhooks", {
        params: { path: { user } },
        body: body as UserWebhook,
      }),
    );
  },
  async updateWebhook(
    user: string,
    webhook: string,
    body: Partial<UserWebhook>,
    updateMask: string,
  ): Promise<UserWebhook> {
    return unwrap(
      await rawClient.PATCH("/api/v1/users/{user}/webhooks/{webhook}", {
        params: { path: { user, webhook }, query: { updateMask } },
        body: body as UserWebhook,
      }),
    );
  },
  async deleteWebhook(user: string, webhook: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/users/{user}/webhooks/{webhook}", {
        params: { path: { user, webhook } },
      }),
    );
  },
  async getWebhookSigningSecret(
    user: string,
    webhook: string,
  ): Promise<string | undefined> {
    const res = await rawClient.GET(
      "/api/v1/users/{user}/webhooks/{webhook}:getSigningSecret",
      {
        params: { path: { user, webhook } },
      },
    );
    return unwrap(res)?.signingSecret;
  },

  // Linked identities (SSO)
  async listLinkedIdentities(
    user: string,
  ): Promise<ListLinkedIdentitiesResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/linkedIdentities", {
        params: { path: { user } },
      }),
    );
  },
  async getLinkedIdentity(
    user: string,
    linkedIdentity: string,
  ): Promise<LinkedIdentity> {
    return unwrap(
      await rawClient.GET(
        "/api/v1/users/{user}/linkedIdentities/{linkedIdentity}",
        {
          params: { path: { user, linkedIdentity } },
        },
      ),
    );
  },
  async createLinkedIdentity(
    user: string,
    body: Record<string, unknown>,
  ): Promise<LinkedIdentity> {
    return unwrap(
      await rawClient.POST("/api/v1/users/{user}/linkedIdentities", {
        params: { path: { user } },
        body: body as never,
      }),
    );
  },
  async deleteLinkedIdentity(
    user: string,
    linkedIdentity: string,
  ): Promise<void> {
    return unwrap(
      await rawClient.DELETE(
        "/api/v1/users/{user}/linkedIdentities/{linkedIdentity}",
        {
          params: { path: { user, linkedIdentity } },
        },
      ),
    );
  },
};

// ===========================================================================
// Shortcuts
// ===========================================================================

export const shortcutService = {
  async list(user: string): Promise<ListShortcutsResponse> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/shortcuts", {
        params: { path: { user } },
      }),
    );
  },
  async get(user: string, shortcut: string): Promise<Shortcut> {
    return unwrap(
      await rawClient.GET("/api/v1/users/{user}/shortcuts/{shortcut}", {
        params: { path: { user, shortcut } },
      }),
    );
  },
  async create(
    user: string,
    body: Partial<Shortcut>,
    validateOnly?: boolean,
  ): Promise<Shortcut> {
    return unwrap(
      await rawClient.POST("/api/v1/users/{user}/shortcuts", {
        params: {
          path: { user },
          query: validateOnly ? { validateOnly } : undefined,
        },
        body: body as Shortcut,
      }),
    );
  },
  async update(
    user: string,
    shortcut: string,
    body: Partial<Shortcut>,
    updateMask: string,
    validateOnly?: boolean,
  ): Promise<Shortcut> {
    return unwrap(
      await rawClient.PATCH("/api/v1/users/{user}/shortcuts/{shortcut}", {
        params: {
          path: { user, shortcut },
          query: { updateMask, validateOnly } as Query,
        },
        body: body as Shortcut,
      }),
    );
  },
  async remove(user: string, shortcut: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/users/{user}/shortcuts/{shortcut}", {
        params: { path: { user, shortcut } },
      }),
    );
  },
};

// ===========================================================================
// Identity providers (admin)
// ===========================================================================

export const identityProviderService = {
  async list(): Promise<ListIdentityProvidersResponse> {
    return unwrap(await rawClient.GET("/api/v1/identity-providers", {}));
  },
  async get(idp: string): Promise<IdentityProvider> {
    return unwrap(
      await rawClient.GET("/api/v1/identity-providers/{identity-provider}", {
        params: { path: { "identity-provider": idp } },
      }),
    );
  },
  async create(
    body: Partial<IdentityProvider>,
    identityProviderId?: string,
  ): Promise<IdentityProvider> {
    return unwrap(
      await rawClient.POST("/api/v1/identity-providers", {
        params: {
          query: identityProviderId ? { identityProviderId } : undefined,
        },
        body: body as IdentityProvider,
      }),
    );
  },
  async update(
    idp: string,
    body: Partial<IdentityProvider>,
    updateMask: string,
  ): Promise<IdentityProvider> {
    return unwrap(
      await rawClient.PATCH("/api/v1/identity-providers/{identity-provider}", {
        params: { path: { "identity-provider": idp }, query: { updateMask } },
        body: body as IdentityProvider,
      }),
    );
  },
  async remove(idp: string): Promise<void> {
    return unwrap(
      await rawClient.DELETE("/api/v1/identity-providers/{identity-provider}", {
        params: { path: { "identity-provider": idp } },
      }),
    );
  },
};

export { ApiError };
